import { useQuery, useQueryClient } from '@tanstack/react-query';
import { type ReactElement, useCallback, useMemo, useState } from 'react';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import { ErrorEventsTable } from '@/features/superuser/components/ErrorEventsTable';
import { SuperuserOrganisationsTable } from '@/features/superuser/components/SuperuserOrganisationsTable';
import { SuperuserUsersTable } from '@/features/superuser/components/SuperuserUsersTable';
import { fetchDiagnosticsReport } from '@/features/superuser/data/diagnostics';
import { getFeatureFlagKeys } from '@/navigation/routes/routeFlags';
import { appRouteDefinitions } from '@/navigation/routes/routes';
import {
  type FeatureFlagUpsertInput,
  queryFeatureFlags,
  upsertFeatureFlag,
} from '@/shared/data/featureFlags';
import { queryKeys } from '@/shared/hooks';
import { useAccessToken } from '@/shared/hooks/auth';
import { resolveSemanticColor } from '@/theme/semanticTokens';
import { s } from '@/theme/tokens';
import { tableTokens } from '@/theme/tokens';
import {
  Box,
  EmptyState,
  formatDate,
  Inline,
  PageHeader,
  Section,
  Stack,
  Surface,
  Text,
} from '@/ui';
import { PageStateScaffold } from '@/ui/composites/scaffolds';
import { layoutModeTokens, useLayoutMode } from '@/ui/layouts/layoutMode';
import { alpha, Button, Switch } from '@/ui/primitives';
import { PageShell } from '@/ui/primitives/PageShell';
import type { AuthSession } from '@/auth/types';
import type { DiagnosticsReportDto, FeatureFlagDto, QueryResult } from '@flowqa/shared';

type SuperuserPageProps = { session: AuthSession | null };

type FeatureFlagRow = FeatureFlagDto & { isPlaceholder?: boolean };

export const SuperuserPage = ({ session }: SuperuserPageProps): ReactElement => {
  const accessToken = useAccessToken();
  const { selectedOrganisationId } = useOrganisationFilter();
  const queryClient = useQueryClient();
  const layoutMode = useLayoutMode();
  const [togglingKey, setTogglingKey] = useState<string | null>(null);
  const knownFlagKeys = useMemo(
    () =>
      getFeatureFlagKeys(appRouteDefinitions, [
        'operations',
        'delivery',
        'users',
        'notifications',
        'organisations',
        'organisationSelector',
        'projectSelector',
        'deltas',
        'themeModeSwitch',
      ]),
    [],
  );
  const filteredKnownKeys = useMemo(() => knownFlagKeys, [knownFlagKeys]);

  const queryInput = useMemo(() => {
    return {
      page: 1,
      pageSize: 100,
      sorts: [{ field: 'key', direction: 'asc' as const }],
      filters: [],
    };
  }, []);
  const searchKey = null;

  const flagsQuery = useQuery<QueryResult<FeatureFlagDto>>({
    queryKey: queryKeys.featureFlags(searchKey),
    queryFn: async () => queryFeatureFlags(queryInput),
    enabled: Boolean(accessToken),
  });
  const diagnosticsQuery = useQuery<DiagnosticsReportDto>({
    queryKey: queryKeys.superuserDiagnostics(),
    queryFn: async () => {
      if (!accessToken) {
        throw new Error('Access token required');
      }
      return await fetchDiagnosticsReport();
    },
    enabled: Boolean(accessToken),
  });

  const handleToggle = useCallback(
    async (flag: FeatureFlagDto): Promise<void> => {
      if (!accessToken) return;
      const toggleKey = flag.key;
      setTogglingKey(toggleKey);
      try {
        const payload: FeatureFlagUpsertInput = {
          key: flag.key,
          enabled: !flag.enabled,
        };
        await upsertFeatureFlag(payload);
        await queryClient.invalidateQueries({ queryKey: queryKeys.featureFlagsPrefix() });
      } finally {
        setTogglingKey(null);
      }
    },
    [queryClient],
  );

  const rows = flagsQuery.data?.items ?? [];
  const allRows = useMemo<FeatureFlagRow[]>(() => {
    const existingKeys = new Set(rows.map((row) => row.key));
    const placeholders = filteredKnownKeys
      .filter((key) => !existingKeys.has(key))
      .map(
        (key): FeatureFlagRow => ({
          id: `virtual-${key}`,
          key,
          enabled: false,
          createdAt: new Date(0),
          updatedAt: new Date(0),
          isPlaceholder: true,
        }),
      );
    return [...rows, ...placeholders].sort((a, b) => a.key.localeCompare(b.key));
  }, [filteredKnownKeys, rows]);
  const listColumns = layoutMode === layoutModeTokens.compact ? '1fr' : '1.4fr 0.8fr 0.5fr';
  const diagnostics = diagnosticsQuery.data;
  const diagnosticsChecks = diagnostics?.checks ?? [];
  const diagnosticsOrder = ['database', 'redis', 'uploadQueue', 'storage'] as const;
  const diagnosticsLabelMap: Record<(typeof diagnosticsOrder)[number], string> = {
    database: 'Database',
    redis: 'Redis',
    uploadQueue: 'Upload Queue',
    storage: 'Storage',
  };

  const content = (
    <Section gap="lg">
      <PageHeader title="Superuser" />
      <Section gap="md">
        <Text role="sectionTitle">Feature Flags</Text>
        {flagsQuery.isLoading ? (
          <Text role="body" color="textSecondary">
            Loading flags...
          </Text>
        ) : allRows.length === 0 ? (
          <EmptyState
            title="No feature flags available."
            body="Create a new flag to start controlling access."
          />
        ) : layoutMode === layoutModeTokens.compact ? (
          <Stack gap="sm">
            {allRows.map((flag) => {
              const toggleKey = flag.key;
              return (
                <Surface key={toggleKey} padding="md">
                  <Section gap="sm">
                    <Text role="sectionTitle">{flag.key}</Text>
                    <Inline gap="sm" align="center" wrap="wrap">
                      <Text role="caption" color="textSecondary">
                        {flag.enabled ? 'Enabled' : 'Disabled'}
                      </Text>
                    </Inline>
                    <Inline gap="sm" align="center">
                      <Switch
                        checked={flag.enabled}
                        onChange={() => {
                          void handleToggle(flag);
                        }}
                        size="small"
                        disabled={togglingKey === toggleKey}
                        slotProps={{ input: { 'aria-label': `Toggle ${flag.key}` } }}
                      />
                    </Inline>
                  </Section>
                </Surface>
              );
            })}
          </Stack>
        ) : (
          <Surface padding="none" border={false} sx={{ width: '100%' }}>
            <Box
              sx={[
                {
                  display: 'grid',
                  gridTemplateColumns: listColumns,
                  gap: s('md'),
                  alignItems: 'center',
                  minHeight: tableTokens.rowHeight,
                  px: s('sm'),
                  py: s('sm'),
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                },
              ]}
            >
              <Text role="overline" color="textSecondary">
                Key
              </Text>
              <Text role="overline" color="textSecondary">
                Status
              </Text>
              <Text role="overline" color="textSecondary">
                Enabled
              </Text>
            </Box>
            {allRows.map((flag) => {
              const toggleKey = flag.key;
              return (
                <Box
                  key={toggleKey}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: listColumns,
                    gap: s('md'),
                    alignItems: 'center',
                    minHeight: tableTokens.rowHeight,
                    px: s('sm'),
                    py: s('sm'),
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    '&:hover': {
                      backgroundColor: (theme) =>
                        alpha(resolveSemanticColor(theme, 'accent'), 0.08),
                    },
                  }}
                >
                  <Text role="body">{flag.key}</Text>
                  <Text role="body" color="textSecondary">
                    {flag.enabled ? 'Enabled' : 'Disabled'}
                  </Text>
                  <Switch
                    checked={flag.enabled}
                    onChange={() => {
                      void handleToggle(flag);
                    }}
                    size="small"
                    disabled={togglingKey === toggleKey}
                    slotProps={{ input: { 'aria-label': `Toggle ${flag.key}` } }}
                  />
                </Box>
              );
            })}
          </Surface>
        )}
      </Section>
      <Section gap="md">
        <Inline align="center" justify="space-between">
          <Text role="sectionTitle">Connectivity</Text>
          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              void diagnosticsQuery.refetch();
            }}
            disabled={diagnosticsQuery.isLoading}
          >
            Refresh
          </Button>
        </Inline>
        {diagnosticsQuery.isLoading ? (
          <Text role="body" color="textSecondary">
            Checking services...
          </Text>
        ) : diagnosticsQuery.isError ? (
          <EmptyState
            title="Unable to load diagnostics."
            body={
              diagnosticsQuery.error instanceof Error
                ? diagnosticsQuery.error.message
                : 'Something went wrong while fetching diagnostics.'
            }
          />
        ) : diagnostics ? (
          <Surface padding="md">
            <Stack gap="sm">
              <Inline align="center" justify="space-between">
                <Text role="body">
                  Overall status: {diagnostics.status === 'ok' ? 'OK' : 'Degraded'}
                </Text>
                <Text role="caption" color="textSecondary">
                  Updated {formatDate(diagnostics.timestamp, { includeTime: true })}
                </Text>
              </Inline>
              <Stack gap="xs">
                {diagnosticsOrder.map((key) => {
                  const check = diagnosticsChecks.find((entry) => entry.key === key) ?? null;
                  const status = check?.status ?? 'skipped';
                  const message = check?.message ?? null;
                  const latency =
                    typeof check?.latencyMs === 'number'
                      ? `${Math.round(check.latencyMs)}ms`
                      : 'n/a';
                  return (
                    <Inline key={key} align="center" justify="space-between">
                      <Stack>
                        <Text role="body">{diagnosticsLabelMap[key]}</Text>
                        {message ? (
                          <Text role="caption" color="textSecondary">
                            {message}
                          </Text>
                        ) : null}
                      </Stack>
                      <Inline gap="sm" align="center">
                        <Text role="caption" color="textSecondary">
                          {latency}
                        </Text>
                        <Text role="caption">{status.toUpperCase()}</Text>
                      </Inline>
                    </Inline>
                  );
                })}
              </Stack>
            </Stack>
          </Surface>
        ) : (
          <EmptyState
            title="Diagnostics not available."
            body="Try refreshing to load connectivity checks."
          />
        )}
      </Section>
      <Section gap="md">
        <ErrorEventsTable />
      </Section>
      <Section gap="md">
        <Text role="sectionTitle">Organisations</Text>
        <SuperuserOrganisationsTable />
      </Section>
      <Section gap="md">
        <Text role="sectionTitle">Users</Text>
        <SuperuserUsersTable />
      </Section>
    </Section>
  );

  return (
    <PageStateScaffold
      session={session}
      sessionMessage="Sign in to manage superuser controls."
      organisationId={selectedOrganisationId}
      projectId={null}
    >
      <PageShell maxWidth="xl" paddingX="lg" paddingY="xl" paddingBottom="xl">
        {content}
      </PageShell>
    </PageStateScaffold>
  );
};
